import React, { useEffect, useState } from 'react';
import { X, Download, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { authHeader } from '../../utils/authHeaders';
import { downloadAuthenticatedFile } from '../../utils/downloadFile';
import { BASE_WORDURL } from '../constants';
import PDFViewer from '../sub-components/PDFViwer';

const PREVIEW_TIMEOUT_MS = 120000;
const MAX_TEXT_CHARS = 200000;
const KIND_BY_EXT = {
    png: 'image', jpg: 'image', jpeg: 'image',
    txt: 'text', json: 'text',
    pdf: 'pdf', xlsx: 'pdf', csv: 'pdf', docx: 'pdf', pptx: 'pdf',
};

export const previewKind = (filename) => KIND_BY_EXT[(filename || '').split('.').pop().toLowerCase()] || null;

export default function AnalysisFilePreview({ doc, onClose, t }) {
    const kind = previewKind(doc?.filename);
    const [status, setStatus] = useState('loading');
    const [imageUrl, setImageUrl] = useState(null);
    const [text, setText] = useState('');
    const [pdfBlob, setPdfBlob] = useState(null);

    useEffect(() => {
        if (!doc?.id || !kind) return undefined;
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), PREVIEW_TIMEOUT_MS);
        let objectUrl = null;
        let cancelled = false;
        const url = `${BASE_WORDURL}/download/${encodeURIComponent(doc.id)}${kind === 'pdf' ? '?format=pdf' : ''}`;
        (async () => {
            try {
                const res = await fetch(url, { headers: { ...authHeader() }, signal: controller.signal });
                if (!res.ok) throw new Error('preview failed');
                const blob = await res.blob();
                if (cancelled) return;
                if (kind === 'image') {
                    if (!blob.type.startsWith('image/')) throw new Error('unexpected type');
                    objectUrl = URL.createObjectURL(blob);
                    setImageUrl(objectUrl);
                } else if (kind === 'text') {
                    const content = await blob.text();
                    if (cancelled) return;
                    setText(content.slice(0, MAX_TEXT_CHARS));
                } else {
                    setPdfBlob(blob);
                }
                setStatus('ready');
            } catch (e) {
                if (!cancelled) setStatus('error');
            } finally {
                clearTimeout(timer);
            }
        })();
        return () => {
            cancelled = true;
            controller.abort();
            clearTimeout(timer);
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [doc?.id, kind]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    if (!doc) return null;

    const handleDownload = () => {
        downloadAuthenticatedFile(`${BASE_WORDURL}/download/${encodeURIComponent(doc.id)}`, doc.filename)
            .catch(() => toast.error(t('dataAnalysis.downloadError')));
    };

    return (
        <div
            className="fixed inset-0 z-[130] flex items-center justify-center bg-black/30 p-4"
            role="dialog"
            aria-modal="true"
            aria-label={doc.filename}
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="flex flex-col w-full max-w-[960px] h-[85vh] bg-white rounded-xl shadow-xl overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E7EB]">
                    <p className="flex-1 min-w-0 truncate text-[14px] font-semibold text-[#111827]" title={doc.filename}>{doc.filename}</p>
                    <button
                        type="button"
                        onClick={handleDownload}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E5E7EB] text-[13px] text-[#374151] hover:bg-[#F9FAFB]"
                    >
                        <Download size={14} />
                        {t('dataAnalysis.download')}
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t('dataAnalysis.closePreview')}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]"
                    >
                        <X size={16} />
                    </button>
                </div>
                <div className="flex-1 min-h-0 overflow-auto bg-[#F9FAFB]">
                    {status === 'loading' && (
                        <div className="h-full flex items-center justify-center gap-2 text-[13px] text-[#6B7280]">
                            <Loader2 size={18} className="animate-spin" />
                            {t('dataAnalysis.previewLoading')}
                        </div>
                    )}
                    {status === 'error' && (
                        <div className="h-full flex items-center justify-center text-[13px] text-[#6B7280] px-6 text-center">
                            {t('dataAnalysis.previewError')}
                        </div>
                    )}
                    {status === 'ready' && kind === 'image' && imageUrl && (
                        <div className="h-full flex items-center justify-center p-4">
                            <img src={imageUrl} alt={doc.filename} className="max-w-full max-h-full object-contain" />
                        </div>
                    )}
                    {status === 'ready' && kind === 'text' && (
                        <pre className="p-4 text-[12.5px] font-mono text-[#111827] whitespace-pre-wrap break-words">{text}</pre>
                    )}
                    {status === 'ready' && kind === 'pdf' && pdfBlob && (
                        <div className="h-full">
                            <PDFViewer file={pdfBlob} initialPage={1} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
